import React from "react";
import { motion } from "framer-motion";
import { FiUsers, FiHeart, FiAward, FiClock, FiChevronRight } from "react-icons/fi";
import CareerForm from "./CareerForm";

const Careers = () => {
  return (
    <div className="min-h-screen bg-gradient-to-r from-blue-600 to-blue-800 pt-20 text-justify">
      {/* Hero Section */}
      <section className="relative py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              JOIN OUR TEAM
            </h1>
            <p className="text-lg text-white max-w-3xl mx-auto">
              At Janatha Automobiles, we believe that our greatest strength lies in our people. We are passionate about fostering a work environment where creativity thrives, innovation is celebrated, and every individual's contribution is valued.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Culture Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Our Culture
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                At Janatha Automobiles, we foster a culture where teamwork, openness, and respect are more than just words — they are the way we work every day.
              </p>
              
              <ul className="space-y-6">
                {[
                  "We encourage collaboration across teams and departments",
                  "We maintain a friendly and open environment where ideas are welcomed, and innovation is celebrated",
                  "We respect the importance of work-life balance, providing flexibility and support to help our employees thrive",
                  "We believe in a non-rigid, humane work culture where individuality is valued, and everyone feels a true sense of belonging",
                  "We are committed to creating a workplace where you feel empowered to grow, succeed, and make a difference"
                ].map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start text-gray-700"
                  >
                    <FiChevronRight className="text-blue-600 mt-1 mr-3 flex-shrink-0" />
                    <span>{item}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-gray-100 rounded-2xl overflow-hidden aspect-video"
            >
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
                alt="Team culture"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gradient-to-r from-blue-900 to-blue-700  ">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              Why Work With Us?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <FiAward className="text-2xl" />,
                title: "Strong Legacy",
                description: "A trusted name in the automotive industry with decades of experience"
              },
              {
                icon: <FiUsers className="text-2xl" />,
                title: "Career Growth",
                description: "We provide opportunities for professional development, skill enhancement, and leadership roles"
              },
              {
                icon: <FiHeart className="text-2xl" />,
                title: "Inclusive Environment",
                description: "A welcoming atmosphere that respects diverse backgrounds and perspectives"
              },
              {
                icon: <FiClock className="text-2xl" />,
                title: "Employee Well-being",
                description: "Flexible work culture designed to support personal and professional lives"
              },
              {
                icon: <FiUsers className="text-2xl" />,
                title: "Team Spirit",
                description: "Be part of a supportive team that values collaboration and collective success"
              }
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-all"
              >
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600 mb-6">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Look For Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Who We Look For
              </h2>
              
              <ul className="space-y-4 text-xl">
                {[
                  "Passionate individuals with a customer-first mindset",
                  "Team players with a positive attitude",
                  "Innovators who are eager to learn and grow",
                  "Professionals who value integrity, commitment, and excellence"
                ].map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start text-gray-700"
                  >
                    <FiChevronRight className="text-blue-600 mt-1 mr-3 flex-shrink-0" />
                    <span>{item}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-gray-100 rounded-2xl overflow-hidden aspect-video order-1 lg:order-2"
            >
              <img
                src="https://images.unsplash.com/photo-1517048676732-d65bc937f952?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTR8fG9mZmljZXxlbnwwfHwwfHx8MA%3D%3D"
                alt="Team working"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16  bg-gradient-to-r from-blue-600 to-blue-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-xl shadow-lg p-8 md:p-12"
          >
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                Apply Now
              </h2>
              <p className="text-gray-600">
                Ready to take the next step in your career? We'd love to hear from you!
              </p>
            </div>
            <CareerForm />
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Careers;